import { useCallback, useEffect, useState, type JSX } from 'react';

import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Trans, useTranslation } from 'react-i18next';

import {
  getNotifications,
  NOTIFICATIONS_PAGE_SIZE,
} from '@/api/services/notificationsService';
import type {
  NotificationLogDto,
  OffsetPage,
} from '@/api/types/Notification';

import { StatusChip, type StatusKey } from '@/components/shell';
import { TableSkeleton } from '@/components/loading';
import { formatDateTimeCs } from '@/utils/date';

// 6 columns (CHR-04: split former "Text" into "Typ zprávy" + new "Text zprávy").
const NOTIFICATIONS_TABLE_COLUMN_WIDTHS = [
  '18%',
  '14%',
  '14%',
  '14%',
  '28%',
  '12%',
];
const NOTIFICATIONS_TABLE_COLSPAN = NOTIFICATIONS_TABLE_COLUMN_WIDTHS.length;
const NOTIFICATIONS_SKELETON_ROWS = 8;

// Notification status → shared StatusChip palette key (DESIGN.md §6.4 + §2).
// SENT → `done` (DCFCE7/15803D, success green) — matches the AdminUsers
// AKTIVNI green; FAILED → `paused` (FEE2E2/B91C1C, error red); SKIPPED_NO_PHONE
// → `waiting` (FEF3C7/B45309, amber) — same amber as the POZVANY chip.
// Unknown/unmapped status falls back to `inactive` (neutral grey) so an
// unexpected api value still renders a chip rather than crashing.
const STATUS_STATUS_KEY: Record<string, StatusKey> = {
  SENT: 'done',
  FAILED: 'paused',
  SKIPPED_NO_PHONE: 'waiting',
};

const statusKeyOf = (status: string): StatusKey =>
  STATUS_STATUS_KEY[status] ?? 'inactive';

// CHR-04: the persisted SMS body is multi-line. Render a stable-height 2-line
// truncated preview (`-webkit-line-clamp`) with an inline "Zobrazit / Skrýt"
// Collapse expand; `body == null` (legacy rows) → "— text neuložen" placeholder.
interface MessageBodyCellProps {
  body: string | null;
}

const MessageBodyCell = ({ body }: MessageBodyCellProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('admin');
  const [expanded, setExpanded] = useState(false);

  if (body == null) {
    return (
      <Box
        component="span"
        sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}
      >
        {t('notifications.textEmpty')}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
      <Collapse in={expanded} collapsedSize="2.8em">
        <Box
          component="span"
          sx={{
            display: 'block',
            fontFamily: theme.monospaceFontFamily,
            fontSize: theme.typography.body2.fontSize,
            lineHeight: 1.4,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            ...(expanded
              ? {}
              : {
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }),
          }}
        >
          {body}
        </Box>
      </Collapse>
      <Link
        component="button"
        type="button"
        underline="hover"
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          alignSelf: 'flex-start',
          minHeight: 44,
          display: 'inline-flex',
          alignItems: 'center',
          fontSize: theme.typography.caption.fontSize,
          fontWeight: 600,
          color: 'primary.main',
        }}
      >
        {expanded
          ? t('notifications.body.collapse')
          : t('notifications.body.expand')}
      </Link>
    </Box>
  );
};

export const AdminNotificationsPage = (): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('admin');

  const [pageData, setPageData] = useState<OffsetPage<NotificationLogDto> | null>(
    null,
  );
  // 0-based page index — maps DIRECTLY to the api `?page=` param (no off-by-one).
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    'idle',
  );

  const reload = useCallback(async (target: number): Promise<void> => {
    setStatus('loading');
    try {
      const data = await getNotifications(target, NOTIFICATIONS_PAGE_SIZE);
      setPageData(data);
      setPage(data.page);
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void reload(0);
  }, [reload]);

  const rows = pageData?.content ?? [];
  const totalElements = pageData?.totalElements ?? 0;
  const totalPages = pageData?.totalPages ?? 0;
  const canPrev = page > 0;
  const canNext = totalPages > 0 && page < totalPages - 1;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
      }}
      data-testid="admin-notifications-page"
    >
      {/* Body: card-wrapped table or error pane (mirrors AdminUsersPage). */}
      {status === 'error' ? (
        <Alert
          severity="error"
          sx={{ mb: theme.spacing(2) }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void reload(page)}
            >
              {t('notifications.list.retry')}
            </Button>
          }
        >
          {t('notifications.list.loadFailed')}
        </Alert>
      ) : (
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3, // 12px (DESIGN.md §4 card radius — matches OrdersTable)
            boxShadow: theme.elevation.shadow1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <TableContainer sx={{ flex: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('notifications.column.recipient')}</TableCell>
                  <TableCell>{t('notifications.column.phone')}</TableCell>
                  <TableCell>{t('notifications.column.sentAt')}</TableCell>
                  <TableCell>{t('notifications.column.text')}</TableCell>
                  <TableCell>{t('notifications.column.body')}</TableCell>
                  <TableCell>{t('notifications.column.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((n) => (
                  <TableRow
                    key={n.id}
                    hover
                    data-testid={`admin-notification-row-${n.id}`}
                    data-status={n.status}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Tooltip title={n.recipientName} placement="top-start">
                        <Box component="span">{n.recipientName}</Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {n.phoneNumber ? (
                        <Box
                          component="span"
                          sx={{
                            fontFamily: theme.monospaceFontFamily,
                            fontFeatureSettings: '"tnum"',
                          }}
                        >
                          {n.phoneNumber}
                        </Box>
                      ) : (
                        <Box
                          component="span"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          {t('notifications.phoneEmpty')}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFeatureSettings: '"tnum"',
                        whiteSpace: 'nowrap',
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {formatDateTimeCs(n.sentAt)}
                    </TableCell>
                    <TableCell>{n.text}</TableCell>
                    <TableCell
                      sx={{ verticalAlign: 'top' }}
                      data-testid={`admin-notification-body-${n.id}`}
                    >
                      <MessageBodyCell body={n.body} />
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        status={statusKeyOf(n.status)}
                        label={t(`notifications.status.${n.status}`, {
                          defaultValue: n.status,
                        })}
                        data-testid={`admin-notification-status-${n.id}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {status === 'ready' && rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={NOTIFICATIONS_TABLE_COLSPAN}
                      sx={{
                        textAlign: 'center',
                        color: theme.palette.text.secondary,
                        py: theme.spacing(4),
                      }}
                    >
                      {t('notifications.list.empty')}
                    </TableCell>
                  </TableRow>
                ) : null}
                {status === 'loading' ? (
                  <TableSkeleton
                    rows={NOTIFICATIONS_SKELETON_ROWS}
                    columnWidths={NOTIFICATIONS_TABLE_COLUMN_WIDTHS}
                  />
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination footer (mockup `.tbl-pagination`) — offset paging,
              page size 50, 0-based `page` straight to the api param. */}
          {status === 'ready' && totalElements > 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(1.5),
                px: theme.spacing(2),
                py: theme.spacing(1.25),
                borderTop: `1px solid ${theme.palette.divider}`,
                fontSize: 12,
                color: theme.palette.text.secondary,
              }}
              data-testid="admin-notifications-pagination"
            >
              <Typography component="span" sx={{ fontSize: 12 }}>
                <Trans
                  i18nKey="admin:notifications.pagination.shown"
                  values={{ count: rows.length, total: totalElements }}
                  components={[
                    <Box
                      key="count"
                      component="b"
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        color: theme.palette.text.primary,
                      }}
                    />,
                    <Box
                      key="total"
                      component="b"
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        color: theme.palette.text.primary,
                      }}
                    />,
                  ]}
                />
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography component="span" sx={{ fontSize: 12 }}>
                {t('notifications.pagination.rowsPerPage')}{' '}
                <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {NOTIFICATIONS_PAGE_SIZE}
                </Box>
              </Typography>
              <Box
                sx={{ display: 'inline-flex', alignItems: 'center', gap: theme.spacing(1) }}
              >
                <IconButton
                  size="small"
                  disabled={!canPrev}
                  onClick={() => void reload(page - 1)}
                  aria-label={t('notifications.pagination.prev')}
                  data-testid="admin-notifications-prev"
                  sx={{ width: 44, height: 44 }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Typography
                  component="span"
                  sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
                  data-testid="admin-notifications-page-indicator"
                >
                  {t('notifications.pagination.pageIndicator', {
                    page: page + 1,
                    total: Math.max(totalPages, 1),
                  })}
                </Typography>
                <IconButton
                  size="small"
                  disabled={!canNext}
                  onClick={() => void reload(page + 1)}
                  aria-label={t('notifications.pagination.next')}
                  data-testid="admin-notifications-next"
                  sx={{ width: 44, height: 44 }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
};
